import * as types from '../../mutation-types';
import BoardsAPI from 'kanban/api/boards';
import PreferencesAPI from 'kanban/api/preferences';
import TasksAPI from 'kanban/api/tasks';
import ProductsAPI from 'kanban/api/products';

// Per-board filter state persisted in the preferences jsonb.
const BOARD_FILTER_KEYS = {
  agentId: 'agent_id',
  inboxId: 'inbox_id',
  showCompleted: 'show_completed',
  showCancelled: 'show_cancelled',
  payload: 'payload',
  q: 'q',
  savedViewId: 'saved_view_id',
};

const FIRST_PAGE_SIZE = 10;
// The tasks endpoint caps per_page here.
const MAX_TASKS_PER_REQUEST = 100;
// Long enough to fold a burst (an automation moving several cards) into one refetch.
const RECONCILE_DELAY_MS = 300;

// Only task events mark something stale: the columns they touch, and the step
// counts. A flush refetches what is marked; a column busy with another load
// stays marked until that load releases it, which flushes again. Nothing polls.
const staleSteps = new Set();
let reconcileTimer = null;
// The store outlives the board page: events keep arriving on other screens,
// and reopening a board reloads it anyway, so nothing is refetched meanwhile.
let boardViewOpen = false;
// One step count refresh in flight at a time, plus one more when events came
// in during it: overlapping ones would each invalidate the one before.
let countsRefresh = null;
let countsStale = false;
// Where each card went, as of the last task event about it (null once it left
// the board), in a sequence every column load reads when it is sent. A load
// answers for the board as it was then, so a card an event has since placed in
// another column, or taken off the board, is dropped from its result; the load
// of the column it went to picks it up.
let placementSeq = 0;
const placedAt = new Map();

const markPlaced = (taskId, stepId) => {
  placementSeq += 1;
  placedAt.set(taskId, { seq: placementSeq, stepId: stepId ?? null });
};

const withoutMovedSince = (tasks, seq, stepId) =>
  tasks.filter(task => {
    const placed = placedAt.get(task.id);
    return !(
      placed &&
      placed.seq > seq &&
      String(placed.stepId) !== String(stepId)
    );
  });

// One page of a column, with the viewer's saved sort and filters.
const requestStepTasks = async (state, stepId, { page, perPage }) => {
  const activeBoardId = state.selectedBoardId;
  const savedSort = state.preferences.task_sorting?.[activeBoardId] || {};
  const sort = savedSort.sort || 'position';
  const order = savedSort.order || 'asc';

  const boardFilters = state.preferences.board_filters?.[activeBoardId] || {};
  const agentId = boardFilters.agent_id;
  const inboxId = boardFilters.inbox_id;

  const requestParams = {
    page,
    perPage,
    sort: sort === 'position' ? undefined : sort,
    order: sort === 'position' ? undefined : order,
    agentId: agentId !== 'all' ? agentId : undefined,
    inboxId: inboxId !== 'all' ? inboxId : undefined,
  };

  const { payload, q } = boardFilters;
  const hasAdvancedFilter = Boolean(payload?.length) || Boolean(q);

  const response = hasAdvancedFilter
    ? await TasksAPI.filterByStep(stepId, { ...requestParams, payload, q })
    : await TasksAPI.getByStep(stepId, requestParams);
  return response.data;
};

// Safety: if the API returns no tasks but claims more, force hasMore off, which
// prevents infinite scroll loops when filters cause count mismatches.
const toStepMeta = (meta, tasks) => ({
  totalCount: meta.total_count,
  page: meta.page,
  perPage: meta.per_page,
  hasMore: meta.has_more && tasks.length > 0,
});

const isSelectedBoardTask = (state, task) =>
  String(task.board_id) === String(state.selectedBoardId);

const findLoadedStepId = (state, taskId) =>
  Object.keys(state.stepTasks).find(stepId =>
    state.stepTasks[stepId]?.some(t => t.id === taskId)
  ) || null;

// Nothing filtered out and no sort column: a card the event names is on screen
// exactly where the viewer's own order puts it, so it can be moved locally.
const showsEveryTaskInPositionOrder = state => {
  const boardId = state.selectedBoardId;
  const filters = state.preferences.board_filters?.[boardId] || {};
  const sort = state.preferences.task_sorting?.[boardId]?.sort || 'position';
  const narrowed =
    (filters.agent_id && filters.agent_id !== 'all') ||
    (filters.inbox_id && filters.inbox_id !== 'all') ||
    Boolean(filters.payload?.length) ||
    Boolean(filters.q);
  return sort === 'position' && !narrowed;
};

export default {
  async fetchBoards({ commit }, params = {}) {
    commit(types.SET_KANBAN_LOADING, true);
    try {
      const response = await BoardsAPI.get(params);
      const { boards, preferences } = response.data;
      if (preferences) {
        commit(types.SET_KANBAN_PREFERENCES, preferences);
      }
      commit(types.SET_BOARDS, boards);
    } catch {
      // Ignore error
    } finally {
      commit(types.SET_KANBAN_LOADING, false);
    }
  },

  async fetchSteps({ commit, state }, { boardId, agentId, inboxId } = {}) {
    const targetBoardId = boardId || state.selectedBoardId;
    if (!targetBoardId) return;

    // Column counts have to come from the same conditions as the card list.
    // fetchTasksForStep reads the persisted filters, so fall back to them when
    // the caller passes nothing (deleteStep, for one) or the badges drift away
    // from the cards. An explicit argument still wins: KanbanBoardPage forces
    // 'all' for viewers restricted to their own cards.
    const boardFilters = state.preferences.board_filters?.[targetBoardId] || {};
    const { payload, q } = boardFilters;
    const hasAdvancedFilter = Boolean(payload?.length) || Boolean(q);
    const effectiveAgentId = agentId ?? boardFilters.agent_id;
    const effectiveInboxId = inboxId ?? boardFilters.inbox_id;

    // Overlapping reloadBoard() calls (change the filter, then the agent) put
    // two step requests in flight for the same board, and the older one must
    // not commit its counts on top of the newer. fetchTasksForStep guards the
    // cards the same way; without this the badges would be the ones drifting.
    commit(types.INCREMENT_STEPS_REQUEST_VERSION);
    const requestVersion = state.stepsRequestVersion;

    try {
      const response = hasAdvancedFilter
        ? await BoardsAPI.filterSteps(targetBoardId, {
            agentId: effectiveAgentId,
            inboxId: effectiveInboxId,
            payload,
            q,
          })
        : await BoardsAPI.getSteps(targetBoardId, {
            agentId: effectiveAgentId,
            inboxId: effectiveInboxId,
          });
      if (
        state.selectedBoardId === targetBoardId &&
        state.stepsRequestVersion === requestVersion
      ) {
        commit(types.SET_STEPS, response.data.steps);
      }
    } catch {
      // Ignore error
    }
  },

  async setActiveBoard({ commit, dispatch }, { boardId, agentId, inboxId }) {
    commit(types.SET_KANBAN_LOADING, true);
    commit(types.SET_SELECTED_BOARD_ID, boardId);
    // Clear steps immediately to prevent stale data triggering task fetches
    commit(types.SET_STEPS, []);
    try {
      // Only fetch steps - tasks will be fetched per step by the component
      await dispatch('fetchSteps', { boardId, agentId, inboxId });
    } finally {
      commit(types.SET_KANBAN_LOADING, false);
    }
  },

  async toggleFavoriteBoard({ commit, state }, boardId) {
    const previousFavorites = [...(state.preferences.favorite_board_ids || [])];
    let newFavorites;

    if (previousFavorites.includes(boardId)) {
      newFavorites = previousFavorites.filter(id => id !== boardId);
    } else {
      newFavorites = [...previousFavorites, boardId];
    }

    commit(types.SET_KANBAN_PREFERENCES, {
      ...state.preferences,
      favorite_board_ids: newFavorites,
    });

    try {
      const response = await BoardsAPI.toggleFavorite(boardId);
      const { favorite_board_ids } = response.data;

      commit(types.SET_KANBAN_PREFERENCES, {
        ...state.preferences,
        favorite_board_ids,
      });
    } catch {
      commit(types.SET_KANBAN_PREFERENCES, {
        ...state.preferences,
        favorite_board_ids: previousFavorites,
      });
    }
  },

  async createBoard({ commit }, boardData) {
    const response = await BoardsAPI.create(boardData);
    commit(types.ADD_BOARD, response.data);
    return response.data;
  },

  async createTask({ commit }, taskData) {
    const response = await TasksAPI.create(taskData);
    commit(types.ADD_TASK, response.data);
    return response.data;
  },

  async updateTask({ commit, state }, { id, task }) {
    // Find task in stepTasks
    let originalTask = null;
    const stepIds = Object.keys(state.stepTasks);
    stepIds.some(stepId => {
      originalTask = state.stepTasks[stepId]?.find(t => t.id === id);
      return !!originalTask;
    });

    if (originalTask) {
      const updates = task ? { ...task } : {};
      const optimisticTask = { ...originalTask, ...updates };
      commit(types.UPDATE_TASK, optimisticTask);
    }

    try {
      const payload = { task };
      const response = await TasksAPI.update(id, payload);
      commit(types.UPDATE_TASK, response.data);
      return response.data;
    } catch (error) {
      if (originalTask) {
        commit(types.UPDATE_TASK, originalTask);
      }
      throw error;
    }
  },

  async moveTask(
    { commit, state, dispatch },
    { taskId, destinationStepId, insertBeforeTaskId, refreshStepId = null }
  ) {
    // Find task in stepTasks
    let task = null;
    const stepIds = Object.keys(state.stepTasks);
    stepIds.some(stepId => {
      task = state.stepTasks[stepId]?.find(t => t.id === taskId);
      return !!task;
    });
    if (!task) return;

    const sourceStepId = task.board_step_id;
    const sourceStep = state.steps.find(s => s.id === sourceStepId);
    const destinationStep = state.steps.find(s => s.id === destinationStepId);

    if (!sourceStep || !destinationStep) return;

    const getTasksOrder = stepId =>
      state.preferences.tasks_order?.[stepId] || [];

    let sourceStepTasksOrder = [...getTasksOrder(sourceStepId)];
    sourceStepTasksOrder = sourceStepTasksOrder.filter(id => id !== taskId);

    let destinationStepTasksOrder;
    if (sourceStepId === destinationStepId) {
      destinationStepTasksOrder = [...sourceStepTasksOrder];
    } else {
      destinationStepTasksOrder = [...getTasksOrder(destinationStepId)];
    }

    if (insertBeforeTaskId) {
      const index = destinationStepTasksOrder.indexOf(insertBeforeTaskId);
      if (index !== -1) {
        destinationStepTasksOrder.splice(index, 0, taskId);
      } else {
        destinationStepTasksOrder.push(taskId);
      }
    } else {
      destinationStepTasksOrder.push(taskId);
    }

    commit(types.MOVE_TASK, {
      task: { ...task, board_step_id: destinationStepId },
      sourceStepId,
      destinationStepId,
      insertBeforeTaskId,
    });

    const newPreferences = {
      ...state.preferences,
      tasks_order: {
        ...(state.preferences.tasks_order || {}),
        [sourceStepId]: sourceStepTasksOrder,
        [destinationStepId]: destinationStepTasksOrder,
      },
    };

    const originalPreferences = { ...state.preferences };
    commit(types.SET_KANBAN_PREFERENCES, newPreferences);

    try {
      await TasksAPI.move(taskId, {
        board_step_id: destinationStepId,
        insert_before_task_id: insertBeforeTaskId,
      });

      // Refresh step tasks after successful move (e.g., when sorting by last activity)
      if (refreshStepId) {
        dispatch('fetchTasksForStep', {
          stepId: refreshStepId,
          page: 1,
          perPage: 10,
        });
      }
    } catch {
      // Revert based on original task state
      // We don't have the original insertBeforeTaskId easily available to restore exact position
      // but UPDATE_TASK will at least put it back in the correct step list
      commit(types.UPDATE_TASK, task);
      commit(types.SET_KANBAN_PREFERENCES, originalPreferences);
    }
  },

  async deleteTask({ commit }, id) {
    await TasksAPI.delete(id);
    commit(types.DELETE_TASK, id);
  },

  async updateStep({ commit, state }, { boardId, stepId, ...data }) {
    const originalStep = state.steps.find(s => s.id === stepId);
    if (originalStep && data.step) {
      const optimisticStep = { ...originalStep, ...data.step };
      commit(types.UPDATE_STEP, optimisticStep);
    }

    try {
      const response = await BoardsAPI.updateStep(boardId, stepId, data);
      commit(types.UPDATE_STEP, response.data);
      return response.data;
    } catch (error) {
      if (originalStep) {
        commit(types.UPDATE_STEP, originalStep);
      }
      throw error;
    }
  },

  async deleteStep({ dispatch, commit }, { boardId, stepId }) {
    await BoardsAPI.deleteStep(boardId, stepId);
    // Reset step tasks and re-fetch steps
    commit(types.RESET_STEP_TASKS);
    await dispatch('fetchSteps', { boardId });
  },

  async createStep({ commit }, { boardId, ...stepData }) {
    const response = await BoardsAPI.createStep(boardId, stepData);
    commit(types.ADD_STEP, response.data);
    return response.data;
  },

  async updateBoard({ commit }, { id, board }) {
    const response = await BoardsAPI.update(id, { board });
    commit(types.UPDATE_BOARD, response.data);
    return response.data;
  },

  async updateBoardAgents({ commit }, { boardId, agentIds }) {
    const response = await BoardsAPI.updateAgents(boardId, agentIds);
    commit(types.UPDATE_BOARD, response.data);
    return response.data;
  },

  async updateBoardInboxes({ commit }, { boardId, inboxIds }) {
    const response = await BoardsAPI.updateInboxes(boardId, inboxIds);
    commit(types.UPDATE_BOARD, response.data);
    return response.data;
  },

  async deleteBoard({ commit }, boardId) {
    await BoardsAPI.delete(boardId);
    commit(types.DELETE_BOARD, boardId);
  },

  // Partial update: only the keys actually passed are touched, so persisting the
  // agent select can't wipe the advanced filter (and vice versa). An explicit
  // empty array or empty string still clears -- only `undefined` means "leave as is".
  async updateBoardFilters({ commit, state }, { boardId, ...changes }) {
    const currentFilters = state.preferences.board_filters?.[boardId] || {};

    const patch = Object.entries(BOARD_FILTER_KEYS).reduce(
      (acc, [camelKey, snakeKey]) => {
        if (changes[camelKey] !== undefined) acc[snakeKey] = changes[camelKey];
        return acc;
      },
      {}
    );

    const nextFilters = { ...currentFilters, ...patch };
    const preferencesToUpdate = { board_filters: { [boardId]: nextFilters } };

    commit(types.SET_KANBAN_PREFERENCES, {
      ...state.preferences,
      board_filters: {
        ...(state.preferences.board_filters || {}),
        [boardId]: nextFilters,
      },
    });

    try {
      await PreferencesAPI.update(preferencesToUpdate);
      return true;
    } catch {
      // Revert this board's filters only. A full snapshot taken before the
      // request would also undo any unrelated preference committed while it
      // was in flight (a favourited board, another board's filters).
      commit(types.SET_KANBAN_PREFERENCES, {
        ...state.preferences,
        board_filters: {
          ...(state.preferences.board_filters || {}),
          [boardId]: currentFilters,
        },
      });
      return false;
    }
  },

  // A task event only says what happened to one card. Where that card belongs
  // on this viewer's board depends on things the event does not carry: the
  // viewer's own saved card order, the active sort and filters, and the pages
  // of each column that were never loaded. So every event ends in a refetch of
  // the columns it touched plus the step counts, and the board converges to
  // what a reload shows. Only the unambiguous case is applied locally first,
  // so the card moves on screen without waiting for the round trip.
  addTaskFromEvent({ dispatch, state }, task) {
    if (!isSelectedBoardTask(state, task)) return;
    dispatch('queueStepReconcile', [task.board_step_id]);
  },

  updateTaskFromEvent({ commit, dispatch, state }, task) {
    const currentStepId = findLoadedStepId(state, task.id);
    if (!isSelectedBoardTask(state, task)) {
      // A transfer names the new funnel; previous_board_id is the one it left.
      if (String(task.previous_board_id) !== String(state.selectedBoardId)) {
        return;
      }
      markPlaced(task.id, null);
      if (currentStepId) commit(types.DELETE_TASK, task.id);
      dispatch('queueStepReconcile', [currentStepId].filter(Boolean));
      return;
    }

    markPlaced(task.id, task.board_step_id);
    dispatch(
      'queueStepReconcile',
      [currentStepId, task.board_step_id].filter(Boolean)
    );
    if (!currentStepId || !showsEveryTaskInPositionOrder(state)) return;

    const newStepId = task.board_step_id;
    const stepChanged = String(currentStepId) !== String(newStepId);

    // Check if this is a move operation (has explicit position info)
    // insert_before_task_id key is only present during move operations
    const hasMovePositionInfo = Object.hasOwn(task, 'insert_before_task_id');

    // If we have position info, use MOVE_TASK (works for both between-step and within-step moves)
    if (hasMovePositionInfo) {
      commit(types.MOVE_TASK, {
        task: { ...task, board_step_id: newStepId },
        sourceStepId: currentStepId,
        destinationStepId: newStepId,
        insertBeforeTaskId: task.insert_before_task_id,
      });
    } else if (stepChanged) {
      // Step changed but no position info - use MOVE_TASK, will append to end
      commit(types.MOVE_TASK, {
        task: { ...task, board_step_id: newStepId },
        sourceStepId: currentStepId,
        destinationStepId: newStepId,
        insertBeforeTaskId: null,
      });
    } else {
      // Same step, no position info - just update properties in place
      commit(types.UPDATE_TASK, task);
    }
  },

  // The delete event carries only the id, so a card from another board, or
  // from a page never loaded, still costs a count refresh. Removing a loaded
  // card is right under any sort or filter, so it happens right away.
  deleteTaskFromEvent({ commit, dispatch, state }, taskId) {
    markPlaced(taskId, null);
    const currentStepId = findLoadedStepId(state, taskId);
    if (currentStepId) commit(types.DELETE_TASK, taskId);
    dispatch('queueStepReconcile', [currentStepId].filter(Boolean));
  },

  // Opening or leaving a board starts the refetch machinery from scratch, so
  // nothing queued for one view (or a count request that never answered)
  // carries over into the next.
  setBoardViewOpen(_context, open) {
    boardViewOpen = open;
    clearTimeout(reconcileTimer);
    reconcileTimer = null;
    staleSteps.clear();
    countsRefresh = null;
    countsStale = false;
    placedAt.clear();
  },

  // What a task event calls: marks the columns it touched and the counts.
  queueStepReconcile({ dispatch }, stepIds) {
    if (!boardViewOpen) return;
    stepIds.forEach(stepId => staleSteps.add(String(stepId)));
    countsStale = true;
    dispatch('scheduleReconcile');
  },

  // The delay runs from the first unflushed mark and is never pushed back, so
  // a burst costs one flush and a steady stream still flushes every
  // RECONCILE_DELAY_MS.
  scheduleReconcile({ dispatch }) {
    if (reconcileTimer) return;
    reconcileTimer = setTimeout(() => {
      reconcileTimer = null;
      dispatch('reconcileStepsWithServer');
    }, RECONCILE_DELAY_MS);
  },

  reconcileStepsWithServer({ dispatch, state }) {
    // Not awaited: under a steady stream of events the counts keep looping, and
    // the cards must not wait for them to go quiet. Asks for nothing unless an
    // event marked the counts since the last refresh.
    dispatch('refreshStepCounts');

    const ready = [...staleSteps].filter(stepId => !state.stepLoading[stepId]);
    ready.forEach(stepId => staleSteps.delete(stepId));
    return Promise.all(
      ready
        .filter(stepId => state.stepFetched[stepId])
        .map(stepId => dispatch('reloadLoadedTasksForStep', stepId))
    );
  },

  refreshStepCounts({ dispatch }) {
    if (countsRefresh) return countsRefresh;
    countsRefresh = (async () => {
      while (countsStale) {
        countsStale = false;
        // eslint-disable-next-line no-await-in-loop
        await dispatch('fetchSteps');
      }
    })().finally(() => {
      countsRefresh = null;
    });
    return countsRefresh;
  },

  // Every column load unlocks through here, so a column an event marked while
  // it was busy gets its refetch as soon as it is free.
  releaseStepLock({ commit, dispatch }, stepId) {
    commit(types.SET_STEP_LOADING, { stepId, isLoading: false });
    if (staleSteps.has(String(stepId))) dispatch('scheduleReconcile');
  },

  // Refetches as many cards as the column already shows (at least a first
  // page), in pages of one size so scrolling on keeps paging from there, and
  // swaps them in only once every page arrived: committing page by page would
  // shrink the column under the viewer's scroll, and a failed page would lose
  // what was on screen.
  async reloadLoadedTasksForStep({ commit, dispatch, state }, stepId) {
    const meta = state.stepMeta[stepId] || {};
    const shown = (meta.page || 1) * (meta.perPage || FIRST_PAGE_SIZE);
    const perPage = Math.min(
      Math.max(shown, FIRST_PAGE_SIZE),
      MAX_TASKS_PER_REQUEST
    );
    const pages = Array.from(
      { length: Math.ceil(shown / perPage) },
      (_, i) => i + 1
    );

    // Same lock as fetchTasksForStep: one load per column at a time. A busy
    // column is skipped by the flush and refetched when it is released.
    commit(types.INCREMENT_STEP_REQUEST_VERSION, stepId);
    const requestVersion = state.stepRequestVersion[stepId];
    commit(types.SET_STEP_LOADING, { stepId, isLoading: true });
    const sentAt = placementSeq;
    let responses = null;
    try {
      responses = await Promise.all(
        pages.map(page => requestStepTasks(state, stepId, { page, perPage }))
      );
    } catch {
      // Keep what is on screen.
    }
    // A board reload (filter or sort change) reset the column and loaded it
    // afresh meanwhile: that load is newer, and its lock is not ours to clear.
    if (state.stepRequestVersion[stepId] !== requestVersion) return;
    dispatch('releaseStepLock', stepId);
    if (!responses) return;

    const seen = new Set();
    const tasks = withoutMovedSince(
      responses.flatMap(response => response.tasks),
      sentAt,
      stepId
    ).filter(task => !seen.has(task.id) && seen.add(task.id));
    commit(types.SET_STEP_TASKS, { stepId, tasks });
    const lastMeta = responses[responses.length - 1].meta;
    if (lastMeta) {
      commit(types.SET_STEP_META, {
        stepId,
        meta: toStepMeta(lastMeta, tasks),
      });
    }
  },

  addStepFromEvent({ commit }, step) {
    commit(types.ADD_STEP, step);
  },

  updateStepFromEvent({ commit }, step) {
    commit(types.UPDATE_STEP, step);
  },

  updateBoardFromEvent({ commit, state, dispatch, rootGetters }, board) {
    // The board.updated broadcast carries global aggregates. For a restricted
    // plain agent, keep the viewer-scoped totals already in state instead of
    // overwriting them with the global ones from the event payload.
    const isRestricted =
      board.settings?.restrict_agent_view_to_own_tasks &&
      rootGetters.getCurrentRole === 'agent' &&
      !rootGetters.getCurrentCustomRoleId;

    const existing = isRestricted
      ? state.boards.find(b => b.id === board.id)
      : null;

    // Cached totals are only viewer-scoped when the board was ALREADY
    // restricted. If it just flipped unrestricted -> restricted, those cached
    // totals are still global, so preserving them would leave stale counts.
    const wasRestricted =
      existing?.settings?.restrict_agent_view_to_own_tasks === true;

    if (existing && wasRestricted) {
      commit(types.UPDATE_BOARD, {
        ...board,
        total_tasks_count: existing.total_tasks_count,
        total_value: existing.total_value,
        total_weighted_value: existing.total_weighted_value,
        steps_summary: existing.steps_summary,
      });
      return;
    }

    if (isRestricted) {
      // Unrestricted -> restricted transition: drop the event's global totals
      // and refetch viewer-scoped stats so the agent doesn't see stale counts.
      commit(types.UPDATE_BOARD, {
        ...board,
        total_tasks_count: undefined,
        total_value: undefined,
        total_weighted_value: undefined,
        steps_summary: undefined,
      });
      dispatch('fetchBoards');
      return;
    }

    commit(types.UPDATE_BOARD, board);
  },

  // Step-based task loading actions
  async fetchTasksForStep(
    { commit, dispatch, state },
    { stepId, page = 1, perPage = 10, append = false }
  ) {
    // Block all concurrent requests while loading (prevents rapid scroll from queueing multiple requests)
    if (state.stepLoading[stepId]) return;

    // Increment request version to invalidate any pending requests
    commit(types.INCREMENT_STEP_REQUEST_VERSION, stepId);
    const requestVersion = state.stepRequestVersion[stepId];

    commit(types.SET_STEP_LOADING, { stepId, isLoading: true });

    const sentAt = placementSeq;
    try {
      const response = await requestStepTasks(state, stepId, {
        page,
        perPage,
      });
      const { meta } = response;
      const tasks = withoutMovedSince(response.tasks, sentAt, stepId);

      // Discard response if a newer request was made
      if (state.stepRequestVersion[stepId] !== requestVersion) {
        return;
      }

      if (append) {
        commit(types.APPEND_STEP_TASKS, { stepId, tasks });
      } else {
        commit(types.SET_STEP_TASKS, { stepId, tasks });
      }

      if (meta) {
        commit(types.SET_STEP_META, { stepId, meta: toStepMeta(meta, tasks) });
      }
    } catch {
      // On error, set hasMore to false to prevent stuck infinite scroll
      if (state.stepRequestVersion[stepId] === requestVersion) {
        commit(types.SET_STEP_META, {
          stepId,
          meta: { ...state.stepMeta[stepId], hasMore: false },
        });
      }
    } finally {
      // Clear the lock unless a board reload replaced this request: the lock
      // is then the new load's.
      if (state.stepRequestVersion[stepId] === requestVersion) {
        dispatch('releaseStepLock', stepId);
      }
    }
  },

  async fetchMoreTasksForStep({ dispatch, state }, stepId) {
    const meta = state.stepMeta[stepId];
    if (!meta || !meta.hasMore) return;

    const nextPage = meta.page + 1;
    await dispatch('fetchTasksForStep', {
      stepId,
      page: nextPage,
      perPage: meta.perPage,
      append: true,
    });
  },

  async initializeStepTasks({ commit, dispatch }, { stepIds }) {
    commit(types.RESET_STEP_TASKS);

    // Fetch first page for each step in parallel
    await Promise.all(
      stepIds.map(stepId =>
        dispatch('fetchTasksForStep', { stepId, page: 1, perPage: 10 })
      )
    );
  },

  resetStepTasks({ commit }) {
    commit(types.RESET_STEP_TASKS);
  },

  async updateTaskSorting({ commit, state }, { boardId, sort, order }) {
    const previousPreferences = { ...state.preferences };
    const newPreferences = {
      ...state.preferences,
      task_sorting: {
        ...(state.preferences.task_sorting || {}),
        [boardId]: { sort, order },
      },
    };
    commit(types.SET_KANBAN_PREFERENCES, newPreferences);

    try {
      await PreferencesAPI.update({
        task_sorting: newPreferences.task_sorting,
      });
    } catch {
      commit(types.SET_KANBAN_PREFERENCES, previousPreferences);
    }
  },

  // Product catalog actions
  async fetchProducts({ commit }, { boardId }) {
    try {
      const response = await ProductsAPI.get(boardId);
      commit(types.SET_PRODUCTS, response.data.products);
    } catch {
      // Ignore error
    }
  },

  async createProduct({ commit }, { boardId, productData }) {
    const response = await ProductsAPI.create(boardId, productData);
    commit(types.ADD_PRODUCT, response.data);
    return response.data;
  },

  async updateProduct({ commit }, { boardId, productId, productData }) {
    const response = await ProductsAPI.update(boardId, productId, productData);
    commit(types.UPDATE_PRODUCT, response.data);
    return response.data;
  },

  async deleteProduct({ commit }, { boardId, productId }) {
    await ProductsAPI.delete(boardId, productId);
    commit(types.DELETE_PRODUCT, productId);
  },
};
